import type { Entry } from "@/shared/types/entries";
import type { AbbreviationsSliceData } from "./slices/abbreviations";
import type { Activity } from "./slices/activities";
import type { Application } from "./slices/applications";
import type { AuthSliceData } from "./slices/auth";
import type { CaseListMatter } from "./slices/caseList";
import type { EntriesQueryScope } from "./slices/entries";
import { fetchAllEntries } from "./slices/entries";
import { EMPTY_ENTRY_FILTERS, fetchEntryFilters } from "./slices/entryFilters";
import type { Matter } from "./slices/matters";
import { EMPTY_REPORTS, parseReportsData } from "./slices/reportsData";
import type { RestrictedWordRule } from "./slices/restrictedWords";
import type { Settings } from "./slices/settings.types";
import { defaultSettings, parseSettings } from "./slices/settings.types";
import type { Task } from "./slices/tasks";
import type { CacheSliceConfig } from "./types";

const defaultAuthData: AuthSliceData = {
	userId: "",
	email: "",
	firstName: null,
	lastName: null,
	displayName: "",
	initials: "?",
	isAdmin: false,
	adminPermissions: {
		overview: false,
		billing: false,
		matters: false,
		entries: false,
		calendar: false,
		support: false,
	},
};

export const cacheSlices = {
	auth: {
		key: "auth",
		fetch: async () => {
			throw new Error("auth slice is populated directly, not fetched");
		},
		initialValue: defaultAuthData as AuthSliceData,
	},

	abbreviations: {
		key: "abbreviations",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/abbreviations",
				method: "GET",
			});
			const data = res.data ?? { enabled: true, abbreviations: [] };
			return {
				enabled: data.enabled,
				abbreviations: (data.abbreviations ?? []).map(
					(a: { id: number; abbreviation: string; expansion: string }) => ({
						id: a.id,
						abbreviation: a.abbreviation,
						expansion: a.expansion,
					}),
				),
			} as AbbreviationsSliceData;
		},
		initialValue: {
			enabled: true,
			abbreviations: [],
		} as AbbreviationsSliceData,
	},

	restricted_words: {
		key: "restricted_words",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/v2/entries/restricted-words?all=true",
				method: "GET",
			});
			const body = res.data as
				| { data?: RestrictedWordRule[] }
				| RestrictedWordRule[]
				| null
				| undefined;
			return Array.isArray(body) ? body : (body?.data ?? []);
		},
		initialValue: [] as RestrictedWordRule[],
	},

	settings: {
		key: "settings",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/settings",
				method: "GET",
			});
			return parseSettings(res.data?.[0]);
		},
		initialValue: defaultSettings as Settings,
	},

	matters: {
		key: "matters",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/matters",
				method: "GET",
			});
			return (res.data ?? []) as Matter[];
		},
		initialValue: [] as Matter[],
	},

	case_list: {
		key: "case_list",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/matters/case-list",
				method: "GET",
			});
			return (res.data ?? []) as CaseListMatter[];
		},
		initialValue: [] as CaseListMatter[],
	},

	tasks: {
		key: "tasks",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/tasks/allTasks",
				method: "GET",
			});
			return (res.data ?? []) as Task[];
		},
		initialValue: [] as Task[],
	},

	activities: {
		key: "activities",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/activities",
				method: "GET",
			});
			return (res.data ?? []) as Activity[];
		},
		initialValue: [] as Activity[],
	},
	applications: {
		key: "applications",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/applications",
				method: "GET",
			});
			return (res.data ?? []) as Application[];
		},
		initialValue: [] as Application[],
	},
	reports: {
		key: "reports",
		fetch: async (api) => {
			const res = await api.makeApiRequest({
				endpoint: "/user/reports",
				method: "GET",
			});
			return parseReportsData(res.data);
		},
		initialValue: EMPTY_REPORTS,
	},
	entry_filters: {
		key: "entry_filters",
		fetch: async (api) => {
			return await fetchEntryFilters(api);
		},
		initialValue: EMPTY_ENTRY_FILTERS,
	},
	entries: {
		key: "entries",
		fetch: async (api, scope) => {
			return await fetchAllEntries(api, scope as EntriesQueryScope | undefined);
		},
		initialValue: [] as Entry[],
	},
} satisfies Record<string, CacheSliceConfig<unknown>>;

export type CacheSliceKey = keyof typeof cacheSlices;
export type CacheSliceData<K extends CacheSliceKey> = Awaited<
	ReturnType<(typeof cacheSlices)[K]["fetch"]>
>;
